import { api } from "@/lib/api-client"

// Read-only effective deployment switches (spec R6, on-prem switch management).
// Mirrors GET /api/admin/deployment-switches in `routes/admin/system.py`, whose
// payload is the boot snapshot taken by `utils/app_setup.capture_deployment_switches`.
//
// The route serves that snapshot instead of reading the environment per request
// (R6.2): QUEUE_BACKEND and COSMOS_BACKEND are module-import-time constants, so
// a request-time read would report what the environment says now rather than
// what the process is running. That gap is the point of the panel — an operator
// who edited a switch without restarting the stack has to be able to see it.

export type DeploymentSwitchSource = "environment" | "default"

export type DeploymentSwitch = {
  name: string
  /** The value this process resolved at boot. */
  value: string
  /** The default the switch's own reader applies when the variable is unset. */
  default: string
  /** `environment` when something set the value; `default` when nothing did. */
  source: DeploymentSwitchSource
  /** True for the switches frozen at module import (QUEUE_BACKEND, COSMOS_BACKEND). */
  frozen_at_import: boolean
  /** True when the environment as it stands can no longer account for `value`. */
  environment_drift: boolean
}

export type DeploymentSwitchesResponse = {
  captured_at: string
  /** The R4.2 on-prem tell: CONFIG_BACKEND=file, i.e. configuration comes from
   * the attested bundle on disk rather than from App Configuration. */
  on_prem: boolean
  switches: DeploymentSwitch[]
  /** Endpoints and credentials that pair with a switch: presence only, never a value (R6.4). */
  companion_settings_present: Record<string, boolean>
  /**
   * Top-level, matching the route. An earlier version of this type declared a
   * nested `uptime` object the route never returns, and the card dereferenced
   * it — so a successful load threw during render and the router's root
   * errorElement replaced the whole admin app. Both suites stayed green
   * because the card's own fixture manufactured the missing object; neither
   * side's tests crossed the seam.
   */
  /**
   * NULLABLE, because the route is: it sends `null` whenever
   * `get_app_start_time()` has not recorded a start (see
   * `routes/admin/system.py`, "started.isoformat() if started else None").
   * Declaring it a bare string is the same cross-seam lie that once made a
   * successful load throw during render.
   */
  process_start_time: string | null
  uptime_seconds: number
  /**
   * Library IDs whose blob container is missing from this stack's local
   * storage (#3071) — empty on every coherent deployment, and only ever
   * non-empty on SEARCH_BACKEND=azure + STORAGE_BACKEND=azurite. There the
   * shared Azure AI Search index returns hits for files the local emulator has
   * never held, so citations 404 and rows without tree_blob_path time the
   * search out. `utils/app_setup.check_library_storage_coherence` probes it at
   * boot and warns; the fix is `poc_deploy_customer.py sync-library-blobs`.
   *
   * NOT optional in this type even though older backends will not send it: the
   * card renders `?? []`, which is the same "nothing to report" either way. A
   * `?`-marked field would instead make every read at the call site a
   * three-way question the card has no better answer for.
   */
  library_storage_mismatch: string[]
}

export async function getDeploymentSwitches(): Promise<DeploymentSwitchesResponse> {
  const response = await api.get("/admin/deployment-switches")
  return response.data || response
}
